<template>
  <div class="receivable-report">
    <header class="page-header">
      <button class="btn-back" @click="$router.go(-1)">←</button>
      <h1>{{ $t('wholesaler.report.ReceivableReport.line5_text') }}</h1>
      <button class="btn-export" @click="exportExcel">📥</button>
    </header>

    <div v-if="loading" class="loading">{{ $t('common.loading') }}</div>

    <div v-else class="report-content">
      <!-- 查询条件 -->
      <div class="filter-bar">
        <div class="filter-row">
          <div class="period-tabs">
            <button :class="['period-tab', filter.period === 'all' ? 'active' : '']" @click="setPeriod('all')">{{ $t('wholesaler.report.ReceivableReport.line16_text') }}</button>
            <button :class="['period-tab', filter.period === 'day' ? 'active' : '']" @click="setPeriod('day')">{{ $t('wholesaler.report.ReceivableReport.line17_text') }}</button>
            <button :class="['period-tab', filter.period === 'week' ? 'active' : '']" @click="setPeriod('week')">{{ $t('wholesaler.report.ReceivableReport.line18_text') }}</button>
            <button :class="['period-tab', filter.period === 'month' ? 'active' : '']" @click="setPeriod('month')">{{ $t('wholesaler.report.ReceivableReport.line19_text') }}</button>
          </div>
        </div>
        <div class="filter-row">
          <div class="customer-search">
            <input
              type="text"
              v-model="filter.keyword"
              :placeholder="$t('wholesaler.report.ReceivableReport.line27_placeholder')"
              class="customer-input"
              @input="onCustomerSearch"
              @focus="onCustomerSearch"
            />
            <button v-if="filter.customerId" class="btn-clear" @click="clearCustomerFilter">✕</button>
          </div>
          <div v-if="showCustomerDropdown && customerOptions.length > 0" class="customer-dropdown">
            <div
              v-for="c in customerOptions"
              :key="c.customerId"
              class="customer-option"
              :class="{ selected: filter.customerId === c.customerId }"
              @click="selectCustomer(c)"
            >
              <span class="option-name">{{ c.customerName }}</span>
              <span class="option-phone">{{ c.customerPhone || '-' }}</span>
            </div>
          </div>
        </div>
        <div v-if="filter.customerId" class="filter-tag">
          <span>当前筛选: {{ filter.customerName }}</span>
          <button class="tag-close" @click="clearCustomerFilter">✕</button>
        </div>
      </div>
      <!-- 统计卡片 -->
      <div class="stats-row">
        <div class="stat-card primary">
          <div class="stat-label">{{ $t('wholesaler.report.ReceivableReport.line55_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(summary.totalReceivable) }}</div>
        </div>
        <div class="stat-card success">
          <div class="stat-label">{{ $t('wholesaler.report.ReceivableReport.line59_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(summary.receivedAmount) }}</div>
        </div>
      </div>
      <div class="stats-row">
        <div class="stat-card warning">
          <div class="stat-label">{{ $t('wholesaler.report.ReceivableReport.line65_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(summary.pendingConfirmAmount) }}</div>
        </div>
        <div class="stat-card danger">
          <div class="stat-label">{{ $t('wholesaler.report.ReceivableReport.line69_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(summary.unpaidAmount) }}</div>
        </div>
      </div>
      <div class="stats-row single">
        <div class="stat-card info">
          <div class="stat-label">{{ $t('wholesaler.report.ReceivableReport.line75_text') }}</div>
          <div class="stat-value">¥{{ formatMoney(summary.refundAmount) }}</div>
        </div>
      </div>

      <!-- 客户列表 -->
      <div class="section-title">{{ $t('wholesaler.report.ReceivableReport.line81_text') }}</div>

      <div v-if="!summary.customerList || summary.customerList.length === 0" class="empty">
        <div class="empty-icon">📦</div>
        <p>{{ $t('common.noData') }}</p>
      </div>

      <div v-for="item in summary.customerList" :key="item.customerId" class="customer-card">
        <div class="card-header" @click="viewCustomerDetail(item)">
          <div class="customer-name">{{ item.customerName }}</div>
          <div class="customer-phone">{{ item.customerPhone || '-' }}</div>
        </div>
        <div class="card-body" @click="viewCustomerDetail(item)">
          <div class="card-stats">
            <div class="card-stat">
              <span class="card-stat-label">{{ $t('wholesaler.report.ReceivableReport.line96_text') }}</span>
              <span class="card-stat-value">¥{{ formatMoney(item.totalReceivable) }}</span>
            </div>
            <div class="card-stat">
              <span class="card-stat-label">{{ $t('wholesaler.report.ReceivableReport.line100_text') }}</span>
              <span class="card-stat-value text-success">¥{{ formatMoney(item.receivedAmount) }}</span>
            </div>
            <div class="card-stat">
              <span class="card-stat-label">{{ $t('wholesaler.report.ReceivableReport.line104_text') }}</span>
              <span class="card-stat-value text-warning">¥{{ formatMoney(item.pendingConfirmAmount) }}</span>
            </div>
            <div class="card-stat">
              <span class="card-stat-label">{{ $t('wholesaler.report.ReceivableReport.line108_text') }}</span>
              <span class="card-stat-value text-danger">¥{{ formatMoney(item.unpaidAmount) }}</span>
            </div>
          </div>
          <div class="card-extra">
            <span>退款: ¥{{ formatMoney(item.refundAmount) }}</span>
            <span>欠款订单: {{ item.unpaidOrderCount }}</span>
            <span>最后下单: {{ item.lastOrderDate || '-' }}</span>
          </div>
        </div>
        <div class="card-actions">
          <button class="card-btn primary" @click.stop="viewCustomerDetail(item)">{{ $t('wholesaler.report.ReceivableReport.line119_text') }}</button>
          <button class="card-btn secondary" @click.stop="printReceivable(item)">{{ $t('wholesaler.report.ReceivableReport.line120_text') }}</button>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="pagination.totalPages > 1" class="pagination">
        <button 
          class="page-btn" 
          :disabled="pagination.page <= 1"
          @click="changePage(pagination.page - 1)">
          上一页
        </button>
        <span class="page-info">{{ pagination.page }} / {{ pagination.totalPages }} 共 {{ pagination.total }} 条</span>
        <button 
          class="page-btn" 
          :disabled="pagination.page >= pagination.totalPages"
          @click="changePage(pagination.page + 1)">
          下一页
        </button>
      </div>
    </div>

    <!-- 弹窗容器 - 会被移到 body 下 -->
    <div class="modal-container" ref="modalContainer">
      <!-- 客户详情弹窗（全屏） -->
      <div v-if="showDetailModal && customerDetail" class="modal-overlay" @click.self="closeDetailModal">
        <div class="modal-content full-modal">
          <div class="modal-header">
            <button class="btn-back" @click="closeDetailModal">←</button>
            <h3>{{ customerDetail.customerName }}</h3>
            <div style="width: 32px;"></div>
          </div>
          <div class="modal-body">
            <!-- 客户汇总 -->
            <div class="detail-stats-row">
              <div class="detail-stat">
                <span class="label">{{ $t('wholesaler.report.ReceivableReport.line156_text') }}</span>
                <span class="value">¥{{ formatMoney(customerDetail.totalReceivable) }}</span>
              </div>
              <div class="detail-stat">
                <span class="label">{{ $t('wholesaler.report.ReceivableReport.line160_text') }}</span>
                <span class="value text-success">¥{{ formatMoney(customerDetail.receivedAmount) }}</span>
              </div>
            </div>
            <div class="detail-stats-row">
              <div class="detail-stat">
                <span class="label">{{ $t('wholesaler.report.ReceivableReport.line166_text') }}</span>
                <span class="value text-warning">¥{{ formatMoney(customerDetail.pendingConfirmAmount) }}</span>
              </div>
              <div class="detail-stat">
                <span class="label">{{ $t('wholesaler.report.ReceivableReport.line170_text') }}</span>
                <span class="value text-danger">¥{{ formatMoney(customerDetail.unpaidAmount) }}</span>
              </div>
            </div>

            <!-- 订单列表 -->
            <div class="section-title">{{ $t('wholesaler.report.ReceivableReport.line176_text') }}</div>

            <div v-if="!customerDetail.unpaidOrders || customerDetail.unpaidOrders.length === 0" class="empty">
              <p>{{ $t('wholesaler.report.ReceivableReport.line179_text') }}</p>
            </div>

            <div v-for="order in customerDetail.unpaidOrders" :key="order.orderId" class="order-card">
              <div class="order-card-header">
                <span class="order-no">{{ order.orderNo }}</span>
                <span class="order-date">{{ order.orderDate }}</span>
              </div>
              <div class="order-card-body">
                <div class="order-stat">
                  <span>{{ $t('wholesaler.report.ReceivableReport.line189_text') }}</span>
                  <span>¥{{ formatMoney(order.orderAmount) }}</span>
                </div>
                <div class="order-stat">
                  <span>{{ $t('wholesaler.report.ReceivableReport.line193_text') }}</span>
                  <span class="text-success">¥{{ formatMoney(order.paidAmount) }}</span>
                </div>
                <div class="order-stat" v-if="order.pendingConfirmAmount > 0">
                  <span>{{ $t('wholesaler.report.ReceivableReport.line197_text') }}</span>
                  <span class="text-warning">¥{{ formatMoney(order.pendingConfirmAmount) }}</span>
                </div>
                <div class="order-stat highlight">
                  <span>{{ $t('wholesaler.report.ReceivableReport.line201_text') }}</span>
                  <span class="text-danger">¥{{ formatMoney(order.unpaidAmount) }}</span>
                </div>
              </div>
              <div class="order-card-actions">
                <button class="card-btn primary" @click="viewOrderDetail(order.orderId)">{{ $t('wholesaler.report.ReceivableReport.line206_text') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.report.ReceivableReport.line226_text') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getReceivableSummary, getCustomerReceivableDetail, searchCustomers } from '@/api/report'
import { getWholesalerId } from '@/utils/wholesaler'
import api from '@/services/api'

export default {
  name: 'ReceivableReport',
  
  data() {
    return {
      loading: false,
      filter: {
        period: 'all',
        keyword: '',
        customerId: null,
        customerName: ''
      },
      showCustomerDropdown: false,
      customerOptions: [],
      searchTimer: null,
      pagination: {
        page: 1,
        size: 20,
        total: 0,
        totalPages: 0
      },
      summary: {
        totalReceivable: 0,
        receivedAmount: 0,
        pendingConfirmAmount: 0,
        unpaidAmount: 0,
        refundAmount: 0,
        customerList: []
      },
      showDetailModal: false,
      customerDetail: {
        customerId: null,
        customerName: '',
        totalReceivable: 0,
        receivedAmount: 0,
        pendingConfirmAmount: 0,
        unpaidAmount: 0,
        refundAmount: 0,
        unpaidOrders: []
      }
    }
  },

  mounted() {
    this.loadReport()
    // 把弹窗容器移到 body 下，避免被 MobileLayout 的 overflow-y:auto 限制
    this.$nextTick(() => {
      if (this.$refs.modalContainer) {
        document.body.appendChild(this.$refs.modalContainer)
      }
    })
  },
  beforeDestroy() {
    if (this.$refs.modalContainer && this.$refs.modalContainer.parentNode === document.body) {
      this.$refs.modalContainer.remove()
    }
  },

  methods: {
    async loadReport() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          wholesalerId: getWholesalerId()
        }
        if (this.filter.period && this.filter.period !== 'all') {
          params.period = this.filter.period
        }
        if (this.filter.customerId) {
          params.customerId = this.filter.customerId
        }
        console.log('📊 加载应收报表, params:', params)
        const res = await getReceivableSummary(params)
        
        if (res && res.success) {
          this.summary = res.data || this.summary
          this.pagination.total = res.total || 0
          this.pagination.totalPages = res.totalPages || 0
          console.log('✅ 报表数据已加载')
        } else {
          console.error('❌ API 返回失败:', res)
          alert(res?.message || '加载报表失败')
        }
      } catch (error) {
        console.error('❌ 加载应收报表失败:', error)
        alert('加载报表失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },

    setPeriod(period) {
      this.filter.period = period
      this.pagination.page = 1
      this.loadReport()
    },

    onCustomerSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(async () => {
        try {
          const res = await searchCustomers(this.filter.keyword)
          if (res && res.success) {
            this.customerOptions = res.data || []
            this.showCustomerDropdown = true
          }
        } catch (e) {
          console.error('搜索客户失败:', e)
        }
      }, 300)
    },

    selectCustomer(c) {
      this.filter.customerId = c.customerId
      this.filter.customerName = c.customerName
      this.filter.keyword = c.customerName
      this.showCustomerDropdown = false
      this.pagination.page = 1
      this.loadReport()
    },

    clearCustomerFilter() {
      this.filter.customerId = null
      this.filter.customerName = ''
      this.filter.keyword = ''
      this.showCustomerDropdown = false
      this.pagination.page = 1
      this.loadReport()
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return
      this.pagination.page = page
      this.loadReport()
    },

    async viewCustomerDetail(item) {
      console.log('📋 点击查看明细, customerId:', item.customerId, 'customerName:', item.customerName)
      try {
        const res = await getCustomerReceivableDetail(item.customerId)
        console.log('📋 明细 API 响应:', res)
        
        // 兼容两种响应格式
        const data = res.success ? res.data : res
        this.customerDetail = data
        this.showDetailModal = true
        console.log('✅ 明细弹窗已打开, 订单数:', data.unpaidOrders?.length)
      } catch (error) {
        console.error('❌ 加载客户详情失败:', error)
        alert('加载客户详情失败: ' + (error.message || '未知错误'))
      }
    },

    async printReceivable(item) {
      console.log('🖨️ 点击打印应收, customerId:', item.customerId)
      try {
        const res = await api.post(`/wholesaler/printer/print-receivable/${item.customerId}`)
        console.log('🖨️ 打印响应:', res)
        
        if (res && res.success) {
          alert('✅ 应收报表已推送到打印机')
        } else {
          alert('❌ 打印失败: ' + (res?.message || '未知错误'))
        }
      } catch (error) {
        console.error('❌ 打印失败:', error)
        alert('打印失败: ' + (error.message || '未知错误'))
      }
    },

    viewOrderDetail(orderId) {
      window.open(`/wholesaler/orders/${orderId}`, '_blank')
    },

    closeDetailModal() {
      this.showDetailModal = false
    },

    exportExcel() {
      const headers = ['客户名称', '联系电话', '应收总额', '已收款', '待确认付款', '未付金额', '退款金额', '欠款订单数', '最后下单']
      const rows = this.summary.customerList.map(item => [
        item.customerName,
        item.customerPhone || '-',
        item.totalReceivable,
        item.receivedAmount,
        item.pendingConfirmAmount,
        item.unpaidAmount,
        item.refundAmount,
        item.unpaidOrderCount,
        item.lastOrderDate || '-'
      ])
      rows.push([])
      rows.push(['合计', '', this.summary.totalReceivable, this.summary.receivedAmount, this.summary.pendingConfirmAmount, this.summary.unpaidAmount, this.summary.refundAmount, '', ''])

      const csvContent = '\uFEFF' + [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `应收报表_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
    },

    formatMoney(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      }
      if (routes[tab]) {
        this.$router.push(routes[tab])
      }
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.receivable-report {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
  max-width: 480px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: white;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 100;
}

.page-header h1 {
  margin: 0;
  font-size: 17px;
  color: #333;
  font-weight: 600;
}

.btn-back {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #333;
  padding: 4px 8px;
}

.btn-export {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 15px;
}

.report-content {
  padding: 12px;
}

/* 查询条件 */
.filter-bar {
  background: white;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.filter-row {
  margin-bottom: 10px;
  position: relative;
}

.filter-row:last-child {
  margin-bottom: 0;
}

.period-tabs {
  display: flex;
  gap: 8px;
}

.period-tab {
  flex: 1;
  padding: 8px 0;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.period-tab.active {
  background: #409EFF;
  color: white;
  border-color: #409EFF;
}

.customer-search {
  position: relative;
}

.customer-input {
  width: 100%;
  padding: 10px 36px 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.customer-input:focus {
  border-color: #409EFF;
}

.btn-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  font-size: 12px;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-clear:active {
  background: #e0e0e0;
}

.customer-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.customer-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}

.customer-option:last-child {
  border-bottom: none;
}

.customer-option:active,
.customer-option.selected {
  background: #f0f7ff;
}

.option-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.option-phone {
  font-size: 12px;
  color: #999;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
  font-size: 12px;
  color: #409EFF;
}

.tag-close {
  border: none;
  background: none;
  color: #409EFF;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  color: #333;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #409EFF;
  color: #409EFF;
}

.page-btn:disabled {
  color: #d9d9d9;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #666;
}

/* 统计卡片 */
.stats-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.stats-row.single {
  justify-content: center;
}

.stat-card {
  flex: 1;
  padding: 12px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-left: 3px solid;
}

.stat-card.primary { border-left-color: #409EFF; }
.stat-card.success { border-left-color: #67C23A; }
.stat-card.warning { border-left-color: #E6A23C; }
.stat-card.danger { border-left-color: #F56C6C; }
.stat-card.info { border-left-color: #909399; }

.stat-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

/* 分区标题 */
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  padding: 16px 0 10px;
}

/* 客户卡片 */
.customer-card {
  background: white;
  border-radius: 10px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px 8px;
  cursor: pointer;
}

.customer-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.customer-phone {
  font-size: 13px;
  color: #999;
}

.card-body {
  padding: 0 14px 10px;
  cursor: pointer;
}

.card-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

.card-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.card-stat-label {
  font-size: 12px;
  color: #999;
}

.card-stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.card-extra {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 11px;
  color: #999;
}

.card-actions {
  display: flex;
  border-top: 1px solid #f0f0f0;
}

.card-btn {
  flex: 1;
  padding: 10px;
  border: none;
  background: none;
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;
}

.card-btn.primary {
  color: #409EFF;
  border-right: 1px solid #f0f0f0;
}

.card-btn.secondary {
  color: #E6A23C;
}

.card-btn:active {
  background: #f5f5f5;
}

.text-success { color: #67C23A; }
.text-warning { color: #E6A23C; }
.text-danger { color: #F56C6C; }

.empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

/* 弹窗 - 全屏 */
.modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  background: rgba(0, 0, 0, 0.5) !important;
  z-index: 999999 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  opacity: 1 !important;
  visibility: visible !important;
  animation: none !important;
}

.modal-content {
  background: white !important;
  width: 100% !important;
  max-width: 480px !important;
  max-height: 95vh !important;
  border-radius: 16px 16px 0 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  opacity: 1 !important;
  visibility: visible !important;
  animation: none !important;
}

.modal-content.full-modal {
  max-height: 100vh !important;
  height: 100vh !important;
  border-radius: 0 !important;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
  font-weight: 600;
}

.modal-body {
  padding: 12px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  text-align: center;
  flex-shrink: 0;
}

/* 客户详情统计 */
.detail-stats-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.detail-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
}

.detail-stat .label {
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
}

.detail-stat .value {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

/* 订单卡片 */
.order-card {
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 10px;
  overflow: hidden;
}

.order-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: white;
}

.order-no {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.order-date {
  font-size: 12px;
  color: #999;
}

.order-card-body {
  padding: 8px 14px;
}

.order-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
  color: #666;
}

.order-stat.highlight {
  font-weight: 600;
  font-size: 14px;
  padding-top: 6px;
  border-top: 1px dashed #e0e0e0;
  margin-top: 4px;
}

.order-card-actions {
  border-top: 1px solid #eee;
  background: white;
}

.order-card-actions .card-btn {
  width: 100%;
  padding: 10px;
  color: #409EFF;
  font-size: 13px;
  border: none;
  background: none;
  cursor: pointer;
}
</style>

<style>
/* 弹窗样式 - 非 scoped，确保移到 body 后仍生效 */
.modal-container .modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  background: rgba(0, 0, 0, 0.5) !important;
  z-index: 99999 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.modal-container .modal-content {
  background: white !important;
  width: 100% !important;
  max-width: 480px !important;
  max-height: 95vh !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}
.modal-container .modal-content.full-modal {
  max-height: 100vh !important;
  height: 100vh !important;
  border-radius: 0 !important;
}
.modal-container .modal-header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #eee !important;
  flex-shrink: 0 !important;
}
.modal-container .modal-header h3 {
  margin: 0 !important;
  font-size: 16px !important;
  color: #333 !important;
  font-weight: 600 !important;
}
.modal-container .modal-body {
  padding: 12px !important;
  overflow-y: auto !important;
  flex: 1 !important;
}
.modal-container .modal-footer {
  padding: 12px 16px !important;
  border-top: 1px solid #eee !important;
  text-align: center !important;
  flex-shrink: 0 !important;
}
</style>
